import { Box, styled } from '@mui/material';
import { useContextMenu } from '../contextMenu/context';
import InfiniteScroll from 'react-infinite-scroll-component';
import EmptyFileArea from './EmptyFileArea';
import { useStorageUnits } from 'contexts/storageUnitContext';
import { useState, useMemo } from 'react';
import FileUploadIndicator from './FileUploadIndicator';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import type { RootState } from 'store';
import { canModifyCurrentLocation } from 'util/access';
import { useSelection } from '../context';

const HEIGHT = 'calc(100vh - 245px)';

const FileDropArea = styled('div')`
  height: ${HEIGHT};
`;

const ScrollWrapper = styled('div')`
  height: ${HEIGHT};
`;

type Props = {
  children: React.ReactNode;
  dataLength: number;
  canFetchMore: boolean;
  fetchMore: () => void;
};

const FileArea = ({ dataLength, canFetchMore, fetchMore, children }: Props) => {
  const { id } = useParams<{ id?: string }>();
  const { openContextMenu } = useContextMenu();
  const { selectedIds } = useSelection();
  const { importFile } = useStorageUnits();
  const [dragActive, setDragActive] = useState(false);
  const { folderPath, directAccess } = useSelector(
    (state: RootState) => state.storageUnitsState,
  );

  const canModify = useMemo(
    () => canModifyCurrentLocation(id, directAccess, folderPath),
    [id, directAccess, folderPath],
  );

  const onContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedIds.length > 0) {
      openContextMenu(null, { x: e.pageX, y: e.pageY });
      return;
    }

    if (!canModify) {
      return;
    }
    openContextMenu(null, { x: e.pageX, y: e.pageY });
  };

  const onUploadFiles = (e: React.DragEvent<HTMLDivElement>) => {
    if (!canModify) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.[0]) {
      Array.from(e.dataTransfer.files).forEach((file) => {
        importFile(file, id);
      });
    }
    setDragActive(false);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    if (!canModify) {
      return;
    }
    if (!e.dataTransfer.types.includes('Files')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <FileDropArea
        onDrop={onUploadFiles}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        data-active={dragActive}
      >
        <InfiniteScroll
          dataLength={dataLength}
          next={fetchMore}
          hasMore={canFetchMore}
          loader={<></>}
          height={HEIGHT}
        >
          <ScrollWrapper onContextMenu={onContextMenu}>
            {children}
          </ScrollWrapper>
          {!dragActive && <EmptyFileArea onContextMenu={onContextMenu} />}
        </InfiniteScroll>
        <FileUploadIndicator active={dragActive} height={HEIGHT} />
      </FileDropArea>
    </Box>
  );
};

export default FileArea;
